import LiveUpdates from './realtime/LiveUpdates';
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { QueryProvider } from './contexts/QueryProvider';
import { LanguageProvider, useLanguage, antdLocaleFor } from './contexts/LanguageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import { UserRole } from './types';
import { landingPathForRole } from './utils/roleLanding';
import './App.css';
// Global mobile/responsive safety net — must come after App.css so it wins
import './responsive.css';
import ErrorBoundary from './components/ErrorBoundary';
import AgencyThemeLoader from './components/AgencyThemeLoader';
import UpdateBanner from './components/UpdateBanner';
import ScrollMemory from './components/ScrollMemory';
import AppShell from './components/AppShell';
import { SHOWCASE_PATH } from './features/showcase/showcaseTracking';

// Code splitting: Lazy load all pages to reduce initial bundle size
// These pages are loaded on-demand when user navigates to them
const LoginPage = React.lazy(() => import(/* webpackChunkName: "login-page" */ './features/auth/LoginPage'));
const AcceptInvitationPage = React.lazy(() => import(/* webpackChunkName: "accept-invitation-page" */ './features/auth/AcceptInvitationPage'));
const DashboardPage = React.lazy(() => import(/* webpackChunkName: "dashboard-page" */ './features/dashboard/DashboardPage'));
const AgenciesPage = React.lazy(() => import(/* webpackChunkName: "agencies-page" */ './features/agencies/AgenciesPage'));
const PatientsPage = React.lazy(() => import(/* webpackChunkName: "patients-page" */ './features/patients/PatientsPage'));
const CaregiversPage = React.lazy(() => import(/* webpackChunkName: "caregivers-page" */ './features/caregivers/CaregiversPage'));
const ShiftsPage = React.lazy(() => import(/* webpackChunkName: "shifts-page" */ './features/shifts/ShiftsPage'));
const InvoicesPage = React.lazy(() => import(/* webpackChunkName: "invoices-page" */ './features/invoices/InvoicesPage'));
const TemplatesPage = React.lazy(() => import(/* webpackChunkName: "templates-page" */ './features/templates/TemplatesPage'));
const PdfAutoFillPage = React.lazy(() => import(/* webpackChunkName: "pdf-auto-fill-page" */ './features/templates/components/PdfAutoFillPage'));
const SettingsPage = React.lazy(() => import(/* webpackChunkName: "settings-page" */ './features/settings/SettingsPage'));
const ManaCareImportPage = React.lazy(() => import(/* webpackChunkName: "mana-care-import-page" */ './features/manacare/ManaCareImportPage'));
const TasksPage = React.lazy(() => import(/* webpackChunkName: "tasks-page" */ './features/tasks/TasksPage'));
const StaffPage = React.lazy(() => import(/* webpackChunkName: "staff-page" */ './features/staff/StaffPage'));
const AgencyMenuConfigPage = React.lazy(() => import(/* webpackChunkName: "agency-menu-config-page" */ './features/admin/AgencyMenuConfigPage'));
const SuperAdminDashboardPage = React.lazy(() => import(/* webpackChunkName: "super-admin-dashboard-page" */ './features/admin/SuperAdminDashboardPage'));
const FilesPage = React.lazy(() => import(/* webpackChunkName: "files-page" */ './features/files/FilesPage'));
const PublicSignaturePage = React.lazy(() => import(/* webpackChunkName: "public-signature-page" */ './features/templates/components/PublicSignaturePage'));
const PatientProfilesPage = React.lazy(() => import(/* webpackChunkName: "patient-profiles-page" */ './features/patients/PatientProfilesPage'));
const MatchingRequestsList = React.lazy(() => import(/* webpackChunkName: "matching-requests-list" */ './features/matching/MatchingRequestsList'));
const CreateMatchingRequest = React.lazy(() => import(/* webpackChunkName: "create-matching-request" */ './features/matching/CreateMatchingRequest'));
const MatchingRequestDetails = React.lazy(() => import(/* webpackChunkName: "matching-request-details" */ './features/matching/MatchingRequestDetails'));
const PublicMatchingResponse = React.lazy(() => import(/* webpackChunkName: "public-matching-response" */ './features/matching/PublicMatchingResponse'));
const PublicLinkResponse = React.lazy(() => import(/* webpackChunkName: "public-link-response" */ './features/matching/PublicLinkResponse'));
const ClientOnboardingPage = React.lazy(() => import(/* webpackChunkName: "client-onboarding-page" */ './features/patients/ClientOnboardingPage'));
const KnowledgePage = React.lazy(() => import(/* webpackChunkName: "knowledge-page" */ './features/knowledge/KnowledgePage'));
const EmailsPage = React.lazy(() => import(/* webpackChunkName: "emails-page" */ './features/emails/EmailsPage'));
const AdminBillingPage = React.lazy(() => import(/* webpackChunkName: "admin-billing-page" */ './features/billing/AdminBillingPage'));
const ClientOverviewPage = React.lazy(() => import(/* webpackChunkName: "client-overview-page" */ './features/patients/ClientOverviewPage'));
const PostingAgenciesPage = React.lazy(() => import(/* webpackChunkName: "posting-agencies-page" */ './features/posting-agencies/PostingAgenciesPage'));
const PostingAgencyDetailPage = React.lazy(() => import(/* webpackChunkName: "posting-agency-detail-page" */ './features/posting-agencies/PostingAgencyDetailPage'));
const CaregiverOverviewPage = React.lazy(() => import(/* webpackChunkName: "caregiver-overview-page" */ './features/caregivers/CaregiverOverviewPage'));
const AgencyBillingPage = React.lazy(() => import(/* webpackChunkName: "agency-billing-page" */ './features/billing/AgencyBillingPage'));
const EmailTriagePage = React.lazy(() => import(/* webpackChunkName: "email-triage-page" */ './features/email-triage/EmailTriagePage'));
const ShowcaseLandingPage = React.lazy(() => import(/* webpackChunkName: "showcase-page" */ './features/showcase/ShowcaseLandingPage'));
const FeaturePreviewPage = React.lazy(() => import(/* webpackChunkName: "feature-preview-page" */ './features/feature-preview/FeaturePreviewPage'));
const EmailTriageAdminPage = React.lazy(() => import(/* webpackChunkName: "email-triage-admin-page" */ './features/admin/EmailTriageAdminPage'));
const HerzensappPage = React.lazy(() => import(/* webpackChunkName: "herzensapp-page" */ './features/herzensapp/HerzensappPage'));
const DgkpPage = React.lazy(() => import(/* webpackChunkName: "dgkp-page" */ './features/herzensapp/DgkpPage'));
const GavPage = React.lazy(() => import(/* webpackChunkName: "gav-page" */ './features/gav/GavPage'));
const HerzensappAdminPage = React.lazy(() => import(/* webpackChunkName: "herzensapp-admin-page" */ './features/admin/HerzensappAdminPage'));
const FeedbackAdminPage = React.lazy(() => import(/* webpackChunkName: "feedback-admin-page" */ './features/admin/FeedbackAdminPage'));
const CustomerNewsletterAdminPage = React.lazy(() => import(/* webpackChunkName: "customer-newsletter-admin-page" */ './features/admin/CustomerNewsletterAdminPage'));
const NewsletterUnsubscribePage = React.lazy(() => import(/* webpackChunkName: "newsletter-unsubscribe-page" */ './features/public/NewsletterUnsubscribePage'));
const DocsPage = React.lazy(() => import(/* webpackChunkName: "docs-page" */ './features/docs/DocsPage'));
const TestimonialsAdminPage = React.lazy(() => import(/* webpackChunkName: "testimonials-admin-page" */ './features/admin/TestimonialsAdminPage'));
const GewerbestatusAdminPage = React.lazy(() => import(/* webpackChunkName: "gewerbestatus-admin-page" */ './features/admin/GewerbestatusAdminPage'));
const DocumentOcrAdminPage = React.lazy(() => import(/* webpackChunkName: "document-ocr-admin-page" */ './features/admin/DocumentOcrAdminPage'));

// Role-aware home: HerzensApp roles (nurse/family) land on /herzensapp,
// everyone else on /dashboard.
const RoleHome: React.FC = () => {
  const { user, isLoading } = useAuth();
  // Without waiting, a fresh app start at "/" (home-screen icon, bookmark)
  // resolved the role before /users/me had loaded and sent 24h-Betreuer to
  // /dashboard -> "Zugriff verweigert".
  if (isLoading) return <PageLoader />;
  return <Navigate to={landingPathForRole(user?.role, user?.permissions)} replace />;
};

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: '#f0f2f5'
  }}>
    <Spin size="large" />
    <span style={{ color: '#666' }}>Seite wird geladen...</span>
  </div>
);

// Inner component to access theme and language context
const AppContent: React.FC = () => {
  const { primaryColor } = useTheme();
  const { language } = useLanguage();
  
  // For demo purposes, using a hardcoded agency ID
  // In a real app, this would come from authentication/context
  const demoAgencyId = '123e4567-e89b-12d3-a456-426614174000';

  return (
    <ConfigProvider
      locale={antdLocaleFor(language)}
      theme={{
        token: {
          colorPrimary: primaryColor,
          borderRadius: 6,
        },
      }}
    >
      <QueryProvider>
        <AuthProvider>
          <AgencyThemeLoader>
            <AntApp>
              <UpdateBanner />
              <LiveUpdates />
              <Router>
                {/* Scroll-Position je Seite merken und beim Zurueckkehren wiederherstellen */}
                <ScrollMemory />
                <div className="App">
                  {/* AppShell holds the sidebar outside <Routes>/<Suspense>,
                      so it is not torn down and rebuilt on every navigation. */}
                  <AppShell>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
                  <Route path="/public-signature/:token/:signatureType" element={<PublicSignaturePage />} />
                  <Route path="/match/:token" element={<PublicMatchingResponse />} />
                  <Route path="/newsletter-abmelden" element={<NewsletterUnsubscribePage />} />
                  <Route path="/match/public/:publicToken" element={<PublicLinkResponse />} />
                  <Route path="/onboarding/:token" element={<ClientOnboardingPage />} />
                  {/* Unlisted "what's new" page for inactive customers (link only in the e-mail) */}
                  <Route path={SHOWCASE_PATH} element={<ShowcaseLandingPage />} />
                  <Route path={`${SHOWCASE_PATH}/vorschau/:featureKey/*`} element={<ShowcaseLandingPage />} />
                  
                  {/* Protected routes */}
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Super Admin only routes */}
                  <Route
                    path="/agencies"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <AgenciesPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/system-settings"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <div>System Settings Page (Coming Soon)</div>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/agency-menu-config"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <AgencyMenuConfigPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin-dashboard"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <SuperAdminDashboardPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Agency Admin and Staff routes (permission-controlled) */}
                  <Route
                    path="/patients"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <PatientsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/patients/:id/overview"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <ClientOverviewPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/caregivers"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <CaregiversPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/posting-agencies"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <PostingAgenciesPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/posting-agencies/:id"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <PostingAgencyDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/caregivers/:id/overview"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <CaregiverOverviewPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/shifts"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <ShiftsPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/invoices"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <InvoicesPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/emails"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <EmailsPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/feature-preview/:featureKey/*"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <FeaturePreviewPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/email-triage"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <EmailTriagePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/email-triage"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <EmailTriageAdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Dokumentation (Pflegedoku + Wundmanagement).
                      Die alte Adresse /herzensapp bleibt als Weiterleitung —
                      Pflegekraefte und Angehoerige haben sie seit Juli als
                      Lesezeichen und in Einladungsmails. */}
                  <Route path="/herzensapp" element={<Navigate to="/documents" replace />} />
                  <Route
                    path="/documents"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          UserRole.AGENCY_ADMIN,
                          UserRole.STAFF,
                          UserRole.NURSE,
                          UserRole.CAREGIVER,
                          UserRole.FAMILY,
                        ]}
                      >
                        <HerzensappPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dgkp"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <DgkpPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* GAV-Zeiterfassung (Schweiz). Die Betreuungskraft erfasst
                      hier ihre Tage, die Disposition plant und gibt frei.
                      Sichtbar nur fuer freigeschaltete Agenturen — der
                      Endpunkt prueft das zusaetzlich serverseitig. */}
                  <Route
                    path="/zeiterfassung"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          UserRole.AGENCY_ADMIN,
                          UserRole.STAFF,
                          UserRole.CAREGIVER,
                          UserRole.NURSE,
                        ]}
                      >
                        <GavPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/herzensapp"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <HerzensappAdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Super-Admin: Rueckmeldungen und Fehlermeldungen aus der App. */}
                  <Route
                    path="/admin/feedback"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <FeedbackAdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Super-Admin: wöchentlicher Kunden-Newsletter (KI-Entwurf, Freigabe). */}
                  <Route
                    path="/admin/newsletter"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <CustomerNewsletterAdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Super-Admin: interne Kosten der Ausweis-/Einkommens-Erkennung (OCR) je Agentur. */}
                  <Route
                    path="/admin/document-ocr"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <DocumentOcrAdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Super-Admin: GISA-Key von HerzensManagement + Gewerbestatus-Nutzung je Agentur. */}
                  <Route
                    path="/admin/gewerbestatus"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <GewerbestatusAdminPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Super-Admin: Video-Erfahrungsberichte der Agenturen (50 € Gutschein). */}
                  <Route
                    path="/admin/testimonials"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <TestimonialsAdminPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/templates"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <TemplatesPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/pdf-autofill"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN, UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <PdfAutoFillPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/tasks"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <TasksPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/knowledge"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <KnowledgePage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/files"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <FilesPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/staff"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <StaffPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Datenuebernahme aus ManaCare. Admin-only: ein Lauf legt
                      tausende Datensaetze an und ist nicht mit einem Klick
                      rueckgaengig zu machen. */}
                  <Route
                    path="/settings/manacare-import"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN]}>
                        <ManaCareImportPage />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/patient-profiles"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN]}>
                        <PatientProfilesPage agencyId={demoAgencyId} />
                      </ProtectedRoute>
                    }
                  />

                  {/* Matching System routes */}
                  <Route
                    path="/matching"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <MatchingRequestsList />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/matching/new"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <CreateMatchingRequest />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/matching/:id"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <MatchingRequestDetails />
                      </ProtectedRoute>
                    }
                  />

                  {/* Billing routes */}
                  <Route
                    path="/admin/billing"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                        <AdminBillingPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/billing"
                    element={
                      <ProtectedRoute allowedRoles={[UserRole.AGENCY_ADMIN, UserRole.STAFF]}>
                        <AgencyBillingPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Handbuch: fuer jeden angemeldeten Benutzer, nie oeffentlich.
                      Jede Seite hat eine eigene Adresse (/doc/<kapitel>/<seite>),
                      Abschnitte sind ueber #anker verlinkbar. Die Inhalte
                      filtert der Server nach Rolle, Rechten und Modulen. */}
                  {['/doc', '/doc/:category', '/doc/:category/:articleId'].map(path => (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            UserRole.SUPER_ADMIN,
                            UserRole.AGENCY_ADMIN,
                            UserRole.STAFF,
                            UserRole.NURSE,
                            UserRole.CAREGIVER,
                            UserRole.FAMILY,
                          ]}
                        >
                          <DocsPage />
                        </ProtectedRoute>
                      }
                    />
                  ))}

                  {/* Default redirect (role-aware) */}
                  <Route path="/" element={<RoleHome />} />

                  {/* Catch all route */}
                  <Route path="*" element={<RoleHome />} />
                </Routes>
                  </Suspense>
                  </AppShell>
              </div>
            </Router>
          </AntApp>
          </AgencyThemeLoader>
        </AuthProvider>
      </QueryProvider>
    </ConfigProvider>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AppContent />
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
